/* Iter 31.125 — Agent Foundry
 *
 * Per Two's brief (30 May 2026): single-page surface for describing
 * an agent, generating their portrait, hearing the description read
 * out via espeak, and either saving (queued for Two to number) or
 * discarding (ephemeral on page refresh).
 *
 * Acceptance test: Gantt chart penguin. Type description, generate,
 * portrait renders, espeak reads. Ships if that works.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Sparkles, Save, Trash2, Volume2, VolumeX } from "lucide-react";
import PerformerOverlay from "@/components/PerformerOverlay";
import DeployStatusBadge from "@/components/DeployStatusBadge";

export default function AgentFoundry() {
    const [description, setDescription] = useState("");
    const [prompt, setPrompt] = useState("");
    const [autoPrompt, setAutoPrompt] = useState(true);
    const [label, setLabel] = useState("");
    // Iter 31.131 — Canon Rule 15: every proposal must answer how it
    // earns / saves time / unlocks revenue for One. Required at save.
    const [revenueLineItem, setRevenueLineItem] = useState("");
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [portrait, setPortrait] = useState(null); // { data_url, prompt_used, byte_size, description }
    const [proposals, setProposals] = useState([]);
    const [muted, setMuted] = useState(false);
    // Iter 31.128 — readKey increments on every generate so the
    // PerformerOverlay re-triggers its espeak readout + animation.
    const [readKey, setReadKey] = useState(0);
    const speakingRef = useRef(false);

    // Iter 31.128 — espeak readout is now driven entirely by
    // PerformerOverlay (jaw tick + head-bob synchronised). The
    // stand-alone speakDescription helper was removed.

    const loadProposals = async () => {
        try {
            const { data } = await api.get("/agent-ledgers/foundry/proposals");
            setProposals(data.proposals || []);
        } catch (_) { /* ignore — empty list is fine */ }
    };

    useEffect(() => { loadProposals(); }, []);

    const handleGenerate = async () => {
        setErr("");
        setPortrait(null);
        if (!description.trim()) {
            setErr("Describe the agent first.");
            return;
        }
        setGenerating(true);
        try {
            const body = { description: description.trim() };
            if (!autoPrompt && prompt.trim()) body.prompt = prompt.trim();
            const { data } = await api.post("/agent-ledgers/foundry/generate", body);
            setPortrait(data);
            // If auto-prompt was on, populate the prompt box with what
            // the system used so One can see and edit it.
            if (autoPrompt && data.prompt_used) setPrompt(data.prompt_used);
            // Iter 31.128 — bump readKey so PerformerOverlay starts the
            // animated readout + jaw tick on the new portrait.
            setReadKey((k) => k + 1);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "generation failed";
            setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setGenerating(false);
        }
    };

    const handleSave = async () => {
        if (!portrait) return;
        const rli = revenueLineItem.trim();
        if (rli.length < 8 || rli.split(/\s+/).length < 2) {
            setErr("Canon Rule 15: revenue_line_item must answer how this agent earns for One, saves One's time, or unlocks revenue downstream. At least 8 chars / 2 words.");
            return;
        }
        setSaving(true);
        setErr("");
        try {
            // Iter 31.127 — multipart upload bypasses the Cloudflare WAF
            // body-size rejection that fires on ~1 MB base64-inlined JSON.
            // Decode the data URL back to bytes and ship as a file part.
            const prefix = "data:image/png;base64,";
            const b64 = portrait.portrait_data_url.startsWith(prefix)
                ? portrait.portrait_data_url.slice(prefix.length)
                : portrait.portrait_data_url;
            const bin = atob(b64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const blob = new Blob([bytes], { type: "image/png" });
            const form = new FormData();
            form.append("description", portrait.description);
            form.append("prompt", portrait.prompt_used);
            form.append("revenue_line_item", revenueLineItem.trim());
            if (label.trim()) form.append("label", label.trim());
            form.append("portrait", blob, "portrait.png");
            await api.post("/agent-ledgers/foundry/proposals-multipart", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Iter 31.127 — espeak confirmation: read back a brief tag
            // so One hears the file land. Per "yes it should" directive.
            try {
                const tag = label.trim() || portrait.description.slice(0, 60);
                if (!muted && "speechSynthesis" in window) {
                    const u = new SpeechSynthesisUtterance(`Filed. ${tag}. Awaiting Two's number.`);
                    u.rate = 0.92; u.pitch = 1.0; u.volume = 0.85;
                    window.speechSynthesis.speak(u);
                }
            } catch (_) { /* ignore */ }
            setPortrait(null);
            setDescription("");
            setPrompt("");
            setLabel("");
            setRevenueLineItem("");
            await loadProposals();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "save failed";
            setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
        } finally {
            setSaving(false);
        }
    };

    const handleDiscard = () => {
        setPortrait(null);
        setDescription("");
        setPrompt("");
        setLabel("");
        setRevenueLineItem("");
        setErr("");
        window.speechSynthesis?.cancel();
    };

    const handleDiscardProposal = async (id) => {
        try {
            await api.post(`/agent-ledgers/foundry/proposals/${id}/discard`);
            await loadProposals();
        } catch (_) { /* ignore */ }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-8 font-body text-ink" data-testid="agent-foundry">
            <header className="border-b border-ink/20 pb-4 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <h1 className="font-heading text-4xl uppercase tracking-[0.05em] text-stamp-red">
                        Agent Foundry
                    </h1>
                    {/* Iter 31.132f — Production deploy status. Silent
                        when preview SHA matches production SHA; renders
                        a red stamp + tooltip when out of sync. Lives
                        here per Six's directive — Foundry is the page
                        most often refreshed after a redeploy. */}
                    <DeployStatusBadge />
                </div>
                <p className="text-sm text-ink-soft mt-2 italic">
                    Describe an agent. Generate their portrait. Hear them. Save or discard.
                    Saved proposals queue for Two to assign a number.
                </p>
            </header>

            <div className="grid md:grid-cols-2 gap-6">
                {/* LEFT: workspace */}
                <section className="space-y-4">
                    <div>
                        <label className="label-typed text-stamp-red block mb-1">Description</label>
                        <textarea
                            data-testid="foundry-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            placeholder='e.g. "a penguin in a tailored suit who runs the Gantt chart"'
                            className="w-full border border-ink/30 bg-paper-light p-3 text-sm font-body focus:outline-none focus:border-stamp-red"
                        />
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-ink-faint">{description.length} chars</span>
                            <button
                                onClick={() => setMuted((m) => !m)}
                                className="text-ink-soft hover:text-stamp-red flex items-center gap-1 text-xs"
                                data-testid="foundry-mute"
                                type="button"
                            >
                                {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                                espeak {muted ? "muted" : "on"}
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="label-typed text-stamp-red">Prompt (Nano Banana)</label>
                            <label className="text-xs text-ink-soft flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    data-testid="foundry-auto-prompt"
                                    checked={autoPrompt}
                                    onChange={(e) => setAutoPrompt(e.target.checked)}
                                />
                                auto from description
                            </label>
                        </div>
                        <textarea
                            data-testid="foundry-prompt"
                            value={prompt}
                            onChange={(e) => { setPrompt(e.target.value); setAutoPrompt(false); }}
                            rows={5}
                            disabled={autoPrompt}
                            placeholder={autoPrompt ? "Auto-derived from description on generate" : "Custom render prompt..."}
                            className="w-full border border-ink/30 bg-paper-light p-3 text-sm font-body focus:outline-none focus:border-stamp-red disabled:opacity-60"
                        />
                    </div>

                    <div>
                        <label className="label-typed text-stamp-red block mb-1">Label (optional)</label>
                        <input
                            data-testid="foundry-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder='e.g. "Gantt-chart Penguin"'
                            className="w-full border border-ink/30 bg-paper-light p-2 text-sm font-body focus:outline-none focus:border-stamp-red"
                        />
                    </div>

                    <div>
                        <label className="label-typed text-stamp-red block mb-1">
                            Revenue line item · <span className="text-ink-soft text-[10px]">Canon Rule 15 · required at save</span>
                        </label>
                        <textarea
                            data-testid="foundry-revenue-line-item"
                            value={revenueLineItem}
                            onChange={(e) => setRevenueLineItem(e.target.value)}
                            rows={2}
                            placeholder='How does this agent earn for One, save One time, or unlock revenue? e.g. "files Two&apos;s sessions to free One&apos;s mental bandwidth ~40min/day"'
                            className="w-full border border-ink/30 bg-paper-light p-2 text-sm font-body focus:outline-none focus:border-stamp-red"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button
                            data-testid="foundry-generate"
                            onClick={handleGenerate}
                            disabled={generating || !description.trim()}
                            className="inline-flex items-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-5 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                        >
                            {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            {generating ? "Rendering…" : "Generate"}
                        </button>
                        {portrait && (
                            <>
                                <button
                                    data-testid="foundry-replay"
                                    onClick={() => setReadKey((k) => k + 1)}
                                    className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em]"
                                    title="Replay the description through espeak"
                                >
                                    <Volume2 className="w-3 h-3" /> Replay
                                </button>
                                <button
                                    data-testid="foundry-save"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 border border-stamp-green text-stamp-green hover:bg-stamp-green hover:text-paper px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                                >
                                    <Save className="w-3 h-3" /> Save proposal
                                </button>
                                <button
                                    data-testid="foundry-discard"
                                    onClick={handleDiscard}
                                    className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em]"
                                >
                                    <Trash2 className="w-3 h-3" /> Discard
                                </button>
                            </>
                        )}
                    </div>

                    {err && (
                        <div className="border-2 border-stamp-red bg-paper p-3 text-sm text-stamp-red" data-testid="foundry-error">
                            {err}
                        </div>
                    )}
                </section>

                {/* RIGHT: portrait preview */}
                <section className="space-y-4">
                    <div
                        className="border border-ink/20 bg-paper-light min-h-[320px] flex items-center justify-center relative"
                        data-testid="foundry-portrait-pane"
                    >
                        {portrait ? (
                            <PerformerOverlay
                                imageUrl={portrait.portrait_data_url}
                                text={portrait.description}
                                playKey={readKey}
                                muted={muted}
                                altText={label || portrait.description.slice(0, 60)}
                                className="max-w-full"
                            />
                        ) : (
                            <div className="text-center px-6 text-ink-faint italic">
                                {generating ? "Rendering portrait…" : "Portrait appears here once generated."}
                            </div>
                        )}
                    </div>
                    {portrait && (
                        <div className="text-xs text-ink-soft" data-testid="foundry-portrait-meta">
                            <div>byte size: {portrait.byte_size?.toLocaleString()}</div>
                            <div>generated_at: {portrait.generated_at}</div>
                        </div>
                    )}
                </section>
            </div>

            {/* PROPOSAL QUEUE */}
            <section className="mt-12">
                <h2 className="font-heading uppercase text-lg tracking-[0.22em] text-ink mb-3">
                    Pending proposals · awaiting Two's number
                </h2>
                {proposals.length === 0 ? (
                    <p className="text-sm text-ink-faint italic" data-testid="foundry-proposals-empty">
                        No pending proposals. Generate and save one to queue for Two.
                    </p>
                ) : (
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="foundry-proposals-list">
                        {proposals.map((p) => (
                            <li key={p.id} className="border border-ink/20 bg-paper-light p-3 flex gap-3" data-testid={`foundry-proposal-${p.id}`}>
                                {p.portrait_url && (
                                    <img
                                        src={p.portrait_url}
                                        alt={p.label || p.description.slice(0, 40)}
                                        className="w-20 h-20 object-cover border border-ink/15 flex-shrink-0"
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading text-sm text-stamp-red truncate" title={p.label || p.description}>
                                        {p.label || p.description.slice(0, 40)}
                                    </div>
                                    <div className="text-xs text-ink-soft line-clamp-3 mt-1">
                                        {p.description}
                                    </div>
                                    {p.revenue_line_item && (
                                        <div className="text-[10px] text-stamp-green border-l-2 border-stamp-green pl-1 mt-1 italic line-clamp-2" data-testid={`foundry-proposal-${p.id}-revenue`}>
                                            £ {p.revenue_line_item}
                                        </div>
                                    )}
                                    <button
                                        onClick={() => handleDiscardProposal(p.id)}
                                        className="text-[10px] uppercase tracking-[0.18em] text-ink-faint hover:text-stamp-red mt-2"
                                        data-testid={`foundry-proposal-${p.id}-discard`}
                                    >
                                        Discard
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
